import { afterRenderEffect, Component, ElementRef, inject, input, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IconModule } from '@insolution/core-data-access';
import { HelpService } from '@insolution/help-data-access';
import { TranslatePipe } from '@ngx-translate/core';
import { MarkdownPipe } from '../pipes/markdown.pipe';
import { EHelpMessageRole } from './enums/help-message-role.enum';
import { IHelpMessage } from './interfaces/help-message.interface';

@Component({
  selector: 'lib-help-launcher',
  imports: [FormsModule, IconModule, TranslatePipe, MarkdownPipe],
  templateUrl: './help-launcher.html',
  styleUrl: './help-launcher.scss',
})
export class HelpLauncher {
  public readonly showSources = input(true);

  private readonly messagesContainer = viewChild<ElementRef<HTMLElement>>('messagesContainer');

  private readonly helpService = inject(HelpService);

  protected readonly eHelpMessageRole = EHelpMessageRole;

  protected readonly isOpen = signal(false);
  protected readonly messages = signal<IHelpMessage[]>([]);
  protected readonly draft = signal('');
  protected readonly isBusy = signal(false);
  protected readonly hasError = signal(false);

  private sessionId: string | undefined;

  constructor() {
    // Runs after Angular has rendered the new message into the DOM, so the
    // element is available to measure and scroll.
    afterRenderEffect(() => {
      const messages = this.messages();
      const lastIndex = messages.length - 1;

      // Only reposition when the user just asked something: scroll their new
      // question to the top so the question and the start of the answer are in
      // view and they can read from the top instead of chasing the bottom.
      if (messages[lastIndex]?.role !== EHelpMessageRole.User) {
        return;
      }

      const container = this.messagesContainer()?.nativeElement;
      const message = container?.querySelector<HTMLElement>(`#help-message-${lastIndex}`);
      if (container && message) {
        this.scrollQuestionToTop(container, message);
      }
    });
  }

  private scrollQuestionToTop(container: HTMLElement, message: HTMLElement): void {
    // Reset any previously reserved space before measuring.
    container.style.paddingBottom = '0px';

    // Leave a little breathing room above the question instead of pinning it
    // flush against the top edge.
    const topOffset = 12;
    const targetScrollTop =
      container.scrollTop + (message.getBoundingClientRect().top - container.getBoundingClientRect().top) - topOffset;
    const maxScrollTop = container.scrollHeight - container.clientHeight;

    // The answer has not arrived yet, so there may not be enough content below
    // the question to scroll it all the way up. Reserve just enough space so it
    // can reach the top of the container.
    const missingSpace = targetScrollTop - maxScrollTop;
    if (missingSpace > 0) {
      container.style.paddingBottom = `${missingSpace}px`;
    }

    container.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
  }

  protected toggle(): void {
    this.isOpen.update((open) => !open);
  }

  protected close(): void {
    this.isOpen.set(false);
  }

  protected clear(): void {
    this.messages.set([]);
    this.sessionId = undefined;
    this.hasError.set(false);
  }

  protected async send(): Promise<void> {
    const question = this.draft().trim();
    if (!question || this.isBusy()) {
      return;
    }

    this.hasError.set(false);
    this.draft.set('');
    this.messages.update((messages) => [...messages, { role: EHelpMessageRole.User, text: question }]);
    this.isBusy.set(true);

    try {
      const answer = await this.helpService.ask(question, this.sessionId);
      this.sessionId = answer.sessionId;
      this.messages.update((messages) => [
        ...messages,
        { role: EHelpMessageRole.Assistant, text: answer.answer, citations: answer.citations, turnId: answer.turnId },
      ]);
    } catch {
      this.hasError.set(true);
    } finally {
      this.isBusy.set(false);
    }
  }

  protected async giveFeedback(messageIndex: number, helpful: boolean): Promise<void> {
    const message = this.messages()[messageIndex];
    if (!message?.turnId || !this.sessionId || message.feedbackGiven) {
      return;
    }

    try {
      await this.helpService.sendFeedback({ sessionId: this.sessionId, turnId: message.turnId, helpful });
      this.messages.update((messages) =>
        messages.map((item, index) => (index === messageIndex ? { ...item, feedbackGiven: true } : item))
      );
    } catch {
      // A failed feedback submission should not disrupt the conversation.
    }
  }
}
